import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Radio, RefreshCw, Search, Filter, Users, AlertTriangle, BellRing, ArrowRightCircle,
  PhoneOff, CircleDot, Brain, Activity, Heart, ShieldAlert, Gauge, Database,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FormField } from "@/components/common/FormSection";
import { api, formatApiError } from "@/lib/api";
import PatientMonitoringCard from "@/pages/rpm/PatientMonitoringCard";
import { INTERVENTION_TYPES, RISK_COLORS } from "@/pages/rpm/constants";

const REFRESH_MS = 30000;

const GROUP_META = [
  { key: "critical", label: "Critical Risk Patients", color: "#EF4444", bg: "#FEF2F2", border: "#FECACA" },
  { key: "high",     label: "High Risk Patients",     color: "#F97316", bg: "#FFF7ED", border: "#FED7AA" },
  { key: "moderate", label: "Moderate Risk Patients", color: "#F59E0B", bg: "#FFFBEB", border: "#FDE68A" },
  { key: "stable",   label: "Stable Patients",        color: "#10B981", bg: "#ECFDF5", border: "#A7F3D0" },
];

function KPI({ icon: Icon, label, value, accent, testId, caption }) {
  return (
    <div className="hah-card p-3.5 flex items-start gap-3" data-testid={testId}>
      <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: `${accent}14`, color: accent }}>
        <Icon className="w-4 h-4" />
      </span>
      <div className="min-w-0">
        <div className="text-2xl font-bold text-[#1F2937] leading-tight">{value ?? "—"}</div>
        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280] mt-0.5">{label}</div>
        {caption && (
          <div className="text-[10px] text-[#94A3B8] mt-0.5 leading-tight" data-testid={testId ? `${testId}-caption` : undefined}>
            {caption}
          </div>
        )}
      </div>
    </div>
  );
}

export default function RpmDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role_code === "admin";
  const [seeding, setSeeding] = useState(false);
  const [data, setData] = useState({ kpis: {}, groups: { critical: [], high: [], moderate: [], stable: [] } });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [facilityFilter, setFacilityFilter] = useState("__all__");
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Intervention dialog
  const [intervFor, setIntervFor] = useState(null);
  const [intervForm, setIntervForm] = useState({ intervention_type: "", notes: "", outcome: "" });
  // Escalate dialog
  const [escalateFor, setEscalateFor] = useState(null);
  const [escalateForm, setEscalateForm] = useState({ alert_id: "", reason: "" });

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const r = await api.get("/rpm/dashboard");
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  const facilities = useMemo(() => {
    const set = new Map();
    for (const g of Object.values(data.groups)) for (const c of g) {
      if (c.facility_id) set.set(c.facility_id, c.facility_name || c.facility_id);
    }
    return Array.from(set, ([id, name]) => ({ id, name }));
  }, [data]);

  const filteredGroups = useMemo(() => {
    const apply = (cards) => cards.filter((c) => {
      if (search && !(`${c.name} ${c.mrn} ${c.primary_diagnosis || ""}`).toLowerCase().includes(search.toLowerCase())) return false;
      if (facilityFilter !== "__all__" && c.facility_id !== facilityFilter) return false;
      return true;
    });
    const out = {};
    for (const k of ["critical", "high", "moderate", "stable"]) out[k] = apply(data.groups[k] || []);
    return out;
  }, [data, search, facilityFilter]);

  async function bulkAckCriticalAlerts(card) {
    try {
      const r = await api.get("/rpm/alerts", { params: { patient_id: card.patient_id, status: "new" } });
      const items = r.data.items || [];
      if (items.length === 0) { toast.message("No new alerts"); return; }
      for (const a of items) {
        await api.post(`/rpm/alerts/${a.id}/acknowledge`, { notes: "Bulk acknowledged from RPM card" });
      }
      toast.success(`Acknowledged ${items.length} alert${items.length > 1 ? "s" : ""}`);
      load(true);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  function openIntervention(card) {
    setIntervFor(card);
    setIntervForm({ intervention_type: "", notes: "", outcome: "" });
  }
  async function saveIntervention() {
    if (!intervForm.intervention_type) { toast.error("Pick an intervention type"); return; }
    try {
      await api.post("/rpm/interventions", {
        patient_id: intervFor.patient_id,
        intervention_type: intervForm.intervention_type,
        notes: intervForm.notes, outcome: intervForm.outcome,
      });
      toast.success("Intervention recorded");
      setIntervFor(null);
      load(true);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function openEscalate(card) {
    // Find latest open critical alert; if none, just open dialog with empty alert_id
    try {
      const r = await api.get("/rpm/alerts", { params: { patient_id: card.patient_id, status: "new" } });
      const items = r.data.items || [];
      setEscalateFor(card);
      setEscalateForm({ alert_id: items[0]?.id || "", reason: "" });
    } catch (_e) {
      setEscalateFor(card);
      setEscalateForm({ alert_id: "", reason: "" });
    }
  }
  async function doEscalate() {
    if (!escalateForm.reason) { toast.error("Reason required"); return; }
    if (!escalateForm.alert_id) { toast.error("No open alert to escalate"); return; }
    try {
      await api.post(`/rpm/alerts/${escalateForm.alert_id}/escalate`, { reason: escalateForm.reason });
      toast.success("Alert escalated to physician");
      setEscalateFor(null);
      load(true);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const k = data.kpis || {};

  async function generateSampleData() {
    if (!window.confirm("Generate fresh sample vitals for all active RPM patients? This refreshes the board with realistic readings from the last 24h.")) return;
    setSeeding(true);
    try {
      const r = await api.post("/rpm/seed-sample-data");
      const d = r.data || {};
      toast.success(`Seeded ${d.readings_inserted} readings across ${d.enrollments_seeded} patients`);
      load(true);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSeeding(false); }
  }

  return (
    <div data-testid="rpm-command-center">
      <PageHeader
        title="RPM Command Center"
        subtitle="Real-time monitoring of active H@H patients · auto-refresh every 30s"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setAutoRefresh((a) => !a)}
              className={`border-[#E5E7EB] ${autoRefresh ? "text-[#0B7E8C]" : "text-[#6B7280]"}`}
              data-testid="rpm-auto-refresh-toggle"
            >
              <CircleDot className={`w-4 h-4 mr-1.5 ${autoRefresh ? "text-[#10B981]" : ""}`} />
              Auto-refresh {autoRefresh ? "ON" : "OFF"}
            </Button>
            <Button variant="outline" onClick={() => load(true)} className="border-[#E5E7EB]" data-testid="rpm-refresh">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            {isAdmin && (
              <Button
                onClick={generateSampleData}
                disabled={seeding}
                className="bg-[#0B7E8C] hover:bg-[#128C97] text-white"
                data-testid="rpm-seed-sample-data"
              >
                <Database className={`w-4 h-4 mr-1.5 ${seeding ? "animate-pulse" : ""}`} />
                {seeding ? "Generating…" : "Generate Sample Data"}
              </Button>
            )}
          </div>
        }
      />

      {/* KPI ROW */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3 mb-4">
        <KPI icon={Users}        label="Total Monitored" value={k.total} accent="#0B7E8C" testId="kpi-total" caption="Active RPM enrollments" />
        <KPI icon={Heart}        label="Stable"          value={k.stable} accent="#10B981" testId="kpi-stable" caption="NEWS2 0–2, no breach" />
        <KPI icon={Activity}     label="Moderate"        value={k.moderate} accent="#F59E0B" testId="kpi-moderate" caption="NEWS2 3–4 or single yellow" />
        <KPI icon={ShieldAlert}  label="High Risk"       value={k.high} accent="#F97316" testId="kpi-high" caption="NEWS2 5–6 or multi-yellow" />
        <KPI icon={AlertTriangle}label="Critical"        value={k.critical} accent="#EF4444" testId="kpi-critical" caption="NEWS2 ≥ 7 or red breach" />
        <KPI icon={BellRing}     label="Active Alerts"   value={k.active_alerts} accent="#EF4444" testId="kpi-alerts" caption="Open / unacknowledged" />
        <KPI icon={BellRing}     label="Critical Alerts" value={k.critical_alerts} accent="#DC2626" testId="kpi-crit-alerts" caption="Red-tier alerts open" />
        <KPI icon={ArrowRightCircle} label="Escalated" value={k.escalated_alerts} accent="#7C3AED" testId="kpi-escalated" caption="Escalated to clinician" />
        <KPI icon={PhoneOff}     label="Missing Readings" value={k.missing_readings} accent="#F59E0B" testId="kpi-missing" caption="Below expected today" />
        <KPI icon={Gauge}        label="Device Issues"    value={k.device_issues} accent="#94A3B8" testId="kpi-device" caption="Inactive > 24h" />
        <KPI icon={Activity}     label="High NEWS2"       value={k.high_news2} accent="#F97316" testId="kpi-news2" caption="NEWS2 ≥ 5 currently" />
        <KPI icon={Brain}        label="AI Deterioration" value={k.ai_deterioration} accent="#8B5CF6" testId="kpi-ai" caption="ML-predicted decline" />
      </div>

      {/* Filters */}
      <div className="hah-card p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F5F7FA] border border-[#E5E7EB] rounded-lg flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-[#94A3B8]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name / MRN / diagnosis…"
            className="bg-transparent outline-none text-sm w-full" data-testid="rpm-search" />
        </div>
        <Select value={facilityFilter} onValueChange={setFacilityFilter}>
          <SelectTrigger className="w-56" data-testid="rpm-facility-filter"><Filter className="w-3.5 h-3.5 mr-1.5" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All facilities</SelectItem>
            {facilities.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Risk-grouped sections */}
      {loading ? (
        <div className="hah-card p-12 text-center text-sm text-[#6B7280]">Loading RPM patients…</div>
      ) : (
        <div className="space-y-5">
          {GROUP_META.map((g) => {
            const cards = filteredGroups[g.key] || [];
            return (
              <section key={g.key} data-testid={`rpm-group-${g.key}`}>
                <div className="flex items-center gap-2 mb-2.5 pl-1">
                  <span className="w-2 h-2 rounded-full" style={{ background: g.color }} />
                  <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: g.color }}>
                    {g.label}
                  </h2>
                  <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                    style={{ background: g.bg, color: g.color, border: `1px solid ${g.border}` }}>
                    {cards.length}
                  </span>
                  <div className="h-px flex-1 bg-[#E5E7EB]" />
                </div>
                {cards.length === 0 ? (
                  <div className="text-xs text-[#94A3B8] italic pl-4 py-3">No patients in this group.</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {cards.map((c) => (
                      <PatientMonitoringCard key={c.patient_id} card={c}
                        onAck={bulkAckCriticalAlerts}
                        onIntervention={openIntervention}
                        onEscalate={openEscalate} />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {/* Intervention dialog */}
      <Dialog open={!!intervFor} onOpenChange={(o) => { if (!o) setIntervFor(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Intervention</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <FormField label="Patient">
              <div className="text-sm font-medium text-[#1F2937]">{intervFor?.name}</div>
            </FormField>
            <FormField label="Type" required>
              <Select value={intervForm.intervention_type} onValueChange={(v) => setIntervForm({ ...intervForm, intervention_type: v })}>
                <SelectTrigger data-testid="rpm-interv-type"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(INTERVENTION_TYPES).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Notes" full>
              <Textarea rows={3} value={intervForm.notes} onChange={(e) => setIntervForm({ ...intervForm, notes: e.target.value })}
                data-testid="rpm-interv-notes" />
            </FormField>
            <FormField label="Outcome" full>
              <Input value={intervForm.outcome} onChange={(e) => setIntervForm({ ...intervForm, outcome: e.target.value })}
                placeholder="e.g. patient reassured, repeat reading scheduled" data-testid="rpm-interv-outcome" />
            </FormField>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIntervFor(null)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={saveIntervention} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="rpm-interv-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Escalate dialog */}
      <Dialog open={!!escalateFor} onOpenChange={(o) => { if (!o) setEscalateFor(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Escalate Alert</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <FormField label="Patient">
              <div className="text-sm font-medium text-[#1F2937]">{escalateFor?.name}</div>
            </FormField>
            <FormField label="Reason" full required>
              <Textarea rows={3} value={escalateForm.reason} onChange={(e) => setEscalateForm({ ...escalateForm, reason: e.target.value })}
                placeholder="Why is this being escalated to a physician?" data-testid="rpm-escalate-reason" />
            </FormField>
            {!escalateForm.alert_id && (
              <div className="text-xs text-[#92400E] bg-[#FFFBEB] border border-[#FDE68A] rounded p-2">
                No open critical alert found. Use RPM Details to escalate a specific alert.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEscalateFor(null)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={doEscalate} disabled={!escalateForm.alert_id} className="bg-[#EF4444] hover:bg-[#DC2626] text-white" data-testid="rpm-escalate-confirm">
              Escalate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
